import React, { useState } from "react";
import { toast } from "react-toastify";
import { useGetOldCertificateListQuery } from "../../../../store/queries/Verification";
import { downloadOldCertificateExcel } from "../api";
import moment from "moment";
import html2pdf from "html2pdf.js";
import { useDispatch, useSelector } from "react-redux";
import { getPrintData } from "../../../Orders/Order/api";
import { updateConfig as updateApprovedConfig } from "../../../../store/slices/Verification/approvedSlice";

const useApprovedDetails = ({
  receiptId,
  gliNumber,
  officeSlipRef,
  StockReceiptRef,
}) => {
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageSize, setCurrentPageSize] = useState(20);
  const [showExcelUploadModal, setShowExcelUploadModal] = useState(false);
  const state = useSelector((state) => state.verificationOldCardReissue);
  const dispatch = useDispatch();

  const paginationOptions = [
    { label: "20 Per page", value: 20 },
    { label: "40 Per page", value: 40 },
    { label: "60 Per page", value: 60 },
  ];

  const {
    data: certificateList = {},
    isFetching,
    refetch,
  } = useGetOldCertificateListQuery(
    {
      receipt_id: receiptId,
      search: search,
      page_size: currentPageSize,
      page: currentPage,
    },
    { skip: !receiptId },
  );

  const tableFields = certificateList?.data?.fields;

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  };

  const handleClearClick = () => {
    setSearch("");
    setCurrentPage(1);
  };

  const handlePageSize = ({ value: page_size }) => {
    setCurrentPageSize(page_size);
    setCurrentPage(1);
  };

  const handlePagination = (page) => {
    setCurrentPage(page);
  };

  const handleCopyText = async (data) => {
    try {
      await navigator.clipboard.writeText(data);
      toast.success("Copied");
    } catch (error) {
      toast.error("Failed to copy");
    }
  };

  const getRow = (field, data) => {
    const rows = {
      common: (field, data) => <p className="pro-mb-0">{data[field]}</p>,
      status: (_, data) =>
        data?.status === "Rejected" ? (
          <span className={"pro-badge badge-danger-outline pro-d-inline-block"}>
            {"Rejected"}
          </span>
        ) : data?.status === "Completed" ? (
          <span className={"outline-success pro-badge pro-d-inline-block"}>
            {"Completed"}
          </span>
        ) : data?.status === "Pending" ? (
          <span className={"outline-warning pro-badge pro-d-inline-block"}>
            {"Pending"}
          </span>
        ) : data?.status === "Cancelled" ? (
          <span className={"outline-warning pro-badge pro-d-inline-block"}>
            {"Cancelled"}
          </span>
        ) : (
          <span className={"outline pro-badge pro-d-inline-block"}>
            {data?.status}
          </span>
        ),
      certificate_number: (field, data) =>
        data?.[field] ? (
          <div className="pro-d-flex pro-items-center pro-gap-4 no-wrap">
            <p className="pro-mb-0">{data[field]}</p>
            <span
              className="cursor-pointer"
              style={{ cursor: "pointer" }}
              onClick={() => handleCopyText(data[field])}
            >
              <span className="material-symbols-outlined">content_copy</span>
            </span>
          </div>
        ) : (
          ""
        ),
    };
    return rows?.[field]?.(field, data) ?? rows["common"](field, data);
  };

  const handleOfficeSlipPrint = () => {
    if (!receiptId) {
      toast.error("Select a stock receipt first");
      return;
    }
    const print_type = "office_slip";
    getPrintData(receiptId, print_type).then((response) => {
      if (response?.data?.status_code === 200) {
        dispatch(
          updateApprovedConfig(
            (state) => (state.stockPrintData = response?.data?.data),
          ),
        );
        toast.success("Office Slip downloaded successfully.");
      } else {
        toast.error("something went wrong");
      }
    });
    const printInvoice = () => {
      const element = officeSlipRef?.current;
      html2pdf(element, {
        margin: [2, 2, 2, 2],
        filename: `${gliNumber || ""}_Office Slip_${moment(Date.now()).format("DD_MM_YYYY")}_${moment(Date.now()).format("HH_mm_ss")}.pdf`,
        image: { type: "jpeg", quality: 1 },
        html2canvas: {
          dpi: 100,
          letterRendering: true,
          useCORS: true,
          logging: true,
          scale: 4,
          scrollY: 0,
        },
        jsPDF: { unit: "mm", format: "a6", orientation: "portrait" },
        pagebreak: { avoid: "", mode: ["avoid-all", "css", "legacy"] },
        DisablePdfCompression: true,
      });
    };
    setTimeout(() => {
      printInvoice();
    }, 500);
  };

  const handleStockReceiptPrint = () => {
    if (!receiptId) {
      toast.error("Select a stock receipt first");
      return;
    }
    const print_type = "stock_receipt";
    getPrintData(receiptId, print_type).then((response) => {
      if (response?.data?.status_code === 200) {
        toast.success("Stock Invoice downloaded successfully");
        dispatch(
          updateApprovedConfig(
            (state) => (state.stockPrintData = response?.data?.data),
          ),
        );
      } else {
        toast.error("something went wrong");
      }
    });
    const printInvoice = () => {
      const element = StockReceiptRef?.current;
      html2pdf(element, {
        margin: [5, 2, 2, 2],
        filename: `${gliNumber || ""}_Stock Receipt Slip_${moment(Date.now()).format("DD_MM_YYYY")}_${moment(Date.now()).format("HH_mm_ss")}.pdf`,
        image: { type: "jpeg", quality: 1 },
        html2canvas: {
          dpi: 100,
          letterRendering: true,
          useCORS: true,
          logging: true,
          scale: 4,
          scrollY: 0,
        },
        jsPDF: { unit: "mm", format: "a5", orientation: "portrait" },
        pagebreak: { avoid: "", mode: ["avoid-all", "css", "legacy"] },
        DisablePdfCompression: true,
      })
        .from("element-to-print")
        .outputPdf() // add this to replace implicite .save() method, which triggers file download
        .get("pdf")
        .then(function (pdfObj) {
          pdfObj.autoPrint();
          window.open(pdfObj.output("bloburl"), "F");
        });
    };
    setTimeout(() => {
      printInvoice();
    }, 500);
  };

  const handleExcelFileUpload = () => {
    setShowExcelUploadModal((prev) => !prev);
  };

  const handleExcelFileDownload = () => {
    if (!receiptId) {
      toast.error("Select a stock receipt first");
      return;
    }
    downloadOldCertificateExcel(receiptId).then((response) => {
      let dataType = response?.data?.type;
      let binaryData = [];
      binaryData.push(response?.data);
      let downloadLink = document.createElement("a");
      downloadLink.href = window.URL.createObjectURL(
        new Blob(binaryData, { type: dataType }),
      );
      downloadLink.setAttribute(
        "download",
        `${state?.verificationData?.Approved?.find((item) => item?._id === receiptId)?.gli_number || ""}_Fresh Excel_${moment(Date.now()).format("DD_MM_YYYY")}_${moment(Date.now()).format("HH_mm_ss")}.xlsx`,
      );
      document.body.appendChild(downloadLink);
      downloadLink.click();
      toast.success("Excel downloaded successfully.");
    });
  };

  return {
    search,
    isFetching,
    tableFields,
    certificateList,
    currentPage,
    paginationOptions,
    showExcelUploadModal,
    getRow,
    refetch,
    handleSearch,
    handleClearClick,
    handlePageSize,
    handlePagination,
    handleExcelFileUpload,
    handleExcelFileDownload,
    handleOfficeSlipPrint,
    handleStockReceiptPrint,
  };
};

export default useApprovedDetails;
